import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Loader2, Search, Filter, Grid3X3, List, SortAsc, SortDesc, Users, ChevronRight } from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { repairGameCovers } from "@/components/admin/games/dialog/services/gameService";
import { fetchBannersByPage } from "@/services/bannersService";
import { PageBanner } from "@/components/common/PageBanner";

import Navbar from "../components/Navbar";
import { GameCard } from "../components/GameCard";

type Game = {
  id: string;
  name: string;
  cover_url: string | null;
  created_at: string;
};

type GameWithMentors = Game & {
  mentorCount: number;
};

type SortOption = 'mentors-desc' | 'name-asc' | 'name-desc' | 'newest' | 'oldest';
type ViewMode = 'grid' | 'list';

const fetchGamesWithMentors = async (): Promise<GameWithMentors[]> => {
  console.log("Fetching games data with mentor counts...");
  
  const { data, error } = await supabase
    .from("games")
    .select("id, name, cover_url, created_at");
  
  if (error) {
    console.error("Error fetching games:", error);
    throw new Error(error.message);
  }
  
  if (!data) return [];
  
  // Buscar número de mentores para cada jogo
  const gamesWithMentorCount = await Promise.all(
    data.map(async (game) => {
      const { data: mentorData, error: mentorError } = await supabase
        .from("mentorship_offerings")
        .select("mentor_id")
        .eq("game_id", game.id)
        .eq("is_active", true);
      
      if (mentorError) {
        console.error("Error fetching mentor count for game:", game.name, mentorError);
        return { ...game, mentorCount: 0 };
      }
      
      const uniqueMentors = new Set(mentorData?.map(offering => offering.mentor_id) || []);
      const mentorCount = uniqueMentors.size;
      
      return { ...game, mentorCount };
    })
  );
  
  console.log("Games data with mentor counts fetched:", gamesWithMentorCount);
  return gamesWithMentorCount;
};

// Componente para modo lista com contador de mentores
const ListGameCard: React.FC<{ game: GameWithMentors; onClick: () => void }> = ({ game, onClick }) => {
  const mentorCount = game.mentorCount;

  return (
    <Card className="bg-[#181D24] border-[#222A35] hover:border-[#00B6F0]/50 transition-all duration-300 cursor-pointer group hover:shadow-lg" onClick={onClick}>
      <CardContent className="p-6">
        <div className="flex items-center gap-6">
          <div className="w-20 h-20 rounded-xl overflow-hidden bg-[#242C37] flex-shrink-0 group-hover:scale-105 transition-transform">
            {game.cover_url ? (
              <img 
                src={game.cover_url} 
                alt={game.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#223342] to-[#1A1F2C]">
                <span className="text-3xl">🎮</span>
              </div>
            )}
          </div>
          
          <div className="flex-1">
            <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-[#51EFFF] transition-colors">{game.name}</h3>
            
            <div className="flex items-center gap-4 text-sm text-gray-400">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-[#00B6F0]" />
                <span className="font-medium group-hover:text-[#00B6F0] transition-colors">
                  {mentorCount} {mentorCount === 1 ? 'mentor' : 'mentores'}
                </span>
              </div>
              
              <span>•</span>
              
              <span>
                Criado em {new Date(game.created_at).toLocaleDateString('pt-BR')}
              </span>
            </div>
          </div>

          {/* Popular Badge */}
          {mentorCount > 5 && (
            <div className="bg-gradient-to-r from-[#00B6F0] to-[#51EFFF] text-black text-xs font-bold px-3 py-1 rounded-full">
              Popular
            </div>
          )}

          {/* Arrow Button */}
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-[#00B6F0]/20 rounded-full flex items-center justify-center group-hover:bg-[#00B6F0] group-hover:shadow-lg transition-all duration-300">
              <ChevronRight className="h-5 w-5 text-[#00B6F0] group-hover:text-black ml-0.5" />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const JogosPage = () => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["games-with-mentors"],
    queryFn: fetchGamesWithMentors,
  });

  // Query para buscar banners da página de jogos
  const { data: banners } = useQuery({
    queryKey: ["banners", "jogos"],
    queryFn: () => fetchBannersByPage("jogos"),
  });
  
  const navigate = useNavigate();
  const [repairAttempted, setRepairAttempted] = React.useState(false);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [sortOption, setSortOption] = useState<SortOption>("mentors-desc");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");

  // Generate dynamic categories based on actual games
  const gameCategories = useMemo(() => {
    if (!data || data.length === 0) {
      return [{ value: 'all', label: 'Todos os Jogos', count: 0 }];
    }

    const categories = [
      { value: 'all', label: 'Todos os Jogos', count: data.length }
    ];

    // Helper function to check if a game name contains any of the keywords
    const gameMatchesKeywords = (gameName: string, keywords: string[]) => {
      return keywords.some(keyword => 
        gameName.toLowerCase().includes(keyword.toLowerCase())
      );
    };

    // Define category mapping with keywords
    const categoryMappings = [
      { 
        value: 'moba', 
        label: 'MOBA', 
        keywords: ['league of legends', 'dota', 'heroes of the storm', 'lol', 'moba'] 
      },
      { 
        value: 'fps', 
        label: 'FPS', 
        keywords: ['valorant', 'cs2', 'counter-strike', 'overwatch', 'apex', 'fps', 'csgo'] 
      },
      { 
        value: 'battle-royale', 
        label: 'Battle Royale', 
        keywords: ['fortnite', 'apex legends', 'pubg', 'warzone', 'battle royale'] 
      },
      { 
        value: 'rpg', 
        label: 'RPG', 
        keywords: ['wow', 'world of warcraft', 'final fantasy', 'genshin', 'rpg', 'ffxiv'] 
      },
      { 
        value: 'mobile', 
        label: 'Mobile', 
        keywords: ['wild rift', 'mobile legends', 'clash royale', 'mobile', 'ml'] 
      },
      { 
        value: 'sports', 
        label: 'Esportes', 
        keywords: ['fifa', 'rocket league', 'nba', 'fc', 'futebol', 'football'] 
      },
      { 
        value: 'fighting', 
        label: 'Luta', 
        keywords: ['street fighter', 'tekken', 'mortal kombat', 'fighting', 'luta'] 
      },
      { 
        value: 'strategy', 
        label: 'Estratégia', 
        keywords: ['starcraft', 'age of empires', 'civilization', 'strategy', 'rts'] 
      },
      { 
        value: 'card', 
        label: 'Card Games', 
        keywords: ['hearthstone', 'magic', 'legends of runeterra', 'card', 'tcg'] 
      },
      { 
        value: 'racing', 
        label: 'Corrida', 
        keywords: ['f1', 'forza', 'gran turismo', 'racing', 'corrida'] 
      }
    ];

    // Count games for each category
    categoryMappings.forEach(mapping => {
      const count = data.filter(game => 
        gameMatchesKeywords(game.name, mapping.keywords)
      ).length;
      
      // Only add category if there are games that match
      if (count > 0) {
        categories.push({
          value: mapping.value,
          label: `${mapping.label} (${count})`,
          count
        });
      }
    });

    return categories;
  }, [data]);

  // Attempt to refetch once if there's an issue with the data
  React.useEffect(() => {
    if (data && data.length > 0 && data.every(game => !game.cover_url)) {
      console.log("All games missing cover URLs, attempting to refetch in 2 seconds...");
      
      if (!repairAttempted) {
        const timer = setTimeout(async () => {
          console.log("Running cover repair utility...");
          await repairGameCovers();
          setRepairAttempted(true);
          refetch();
        }, 2000);
        
        return () => clearTimeout(timer);
      }
    }
  }, [data, refetch, repairAttempted]);

  // Show toast for debugging in development
  React.useEffect(() => {
    if (import.meta.env.DEV && data && data.length > 0) {
      if (data.every(game => !game.cover_url) && repairAttempted) {
        toast({
          title: "Warning",
          description: "We tried to fix the image URLs, but there are still games without images. Check the storage or try recreating the games.",
          variant: "destructive",
        });
      }
    }
  }, [data, repairAttempted]);

  // Filter and sort games
  const filteredAndSortedGames = useMemo(() => {
    if (!data) return [];

    let filtered = data.filter(game => {
      // Search filter
      const matchesSearch = game.name.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Category filter
      let matchesCategory = true;
      if (selectedCategory !== 'all') {
        // Find the selected category mapping
        const categoryMappings = [
          { 
            value: 'moba', 
            keywords: ['league of legends', 'dota', 'heroes of the storm', 'lol', 'moba'] 
          },
          { 
            value: 'fps', 
            keywords: ['valorant', 'cs2', 'counter-strike', 'overwatch', 'apex', 'fps', 'csgo'] 
          },
          { 
            value: 'battle-royale', 
            keywords: ['fortnite', 'apex legends', 'pubg', 'warzone', 'battle royale'] 
          },
          { 
            value: 'rpg', 
            keywords: ['wow', 'world of warcraft', 'final fantasy', 'genshin', 'rpg', 'ffxiv'] 
          },
          { 
            value: 'mobile', 
            keywords: ['wild rift', 'mobile legends', 'clash royale', 'mobile', 'ml'] 
          },
          { 
            value: 'sports', 
            keywords: ['fifa', 'rocket league', 'nba', 'fc', 'futebol', 'football'] 
          },
          { 
            value: 'fighting', 
            keywords: ['street fighter', 'tekken', 'mortal kombat', 'fighting', 'luta'] 
          },
          { 
            value: 'strategy', 
            keywords: ['starcraft', 'age of empires', 'civilization', 'strategy', 'rts'] 
          },
          { 
            value: 'card', 
            keywords: ['hearthstone', 'magic', 'legends of runeterra', 'card', 'tcg'] 
          },
          { 
            value: 'racing', 
            keywords: ['f1', 'forza', 'gran turismo', 'racing', 'corrida'] 
          }
        ];

        const category = categoryMappings.find(cat => cat.value === selectedCategory);
        if (category?.keywords) {
          matchesCategory = category.keywords.some(keyword => 
            game.name.toLowerCase().includes(keyword.toLowerCase())
          );
        }
      }
      
      return matchesSearch && matchesCategory;
    });

    // Sort games
    filtered.sort((a, b) => {
      switch (sortOption) {
        case 'mentors-desc':
          // Primeiro critério: número de mentores (decrescente)
          if (b.mentorCount !== a.mentorCount) {
            return b.mentorCount - a.mentorCount;
          }
          // Segundo critério: ordem alfabética (crescente)
          return a.name.localeCompare(b.name, 'pt-BR');
        case 'name-asc':
          return a.name.localeCompare(b.name, 'pt-BR');
        case 'name-desc':
          return b.name.localeCompare(a.name, 'pt-BR');
        case 'newest':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case 'oldest':
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        default:
          return 0;
      }
    });

    return filtered;
  }, [data, searchTerm, selectedCategory, sortOption]);

  const clearAllFilters = () => {
    setSearchTerm("");
    setSelectedCategory("all");
    setSortOption("mentors-desc");
  };

  const activeFiltersCount = [
    searchTerm !== "",
    selectedCategory !== "all"
  ].filter(Boolean).length;

  return (
    <div className="bg-[#0f141c] min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 pb-12">
        {/* Banner da página */}
        {banners && banners.length > 0 && (
          <PageBanner banners={banners} />
        )}

        <div className="pt-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-extrabold text-[#00B6F0] mb-4">
              Explore os Jogos
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Descubra os melhores jogos e encontre mentores especializados para melhorar suas habilidades
            </p>
          </div>

          {/* Filters Section */}
          <Card className="bg-[#181D24] border-[#222A35] mb-8">
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
                {/* Search */}
                <div className="flex-1 w-full lg:w-auto">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      placeholder="Buscar jogos..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 bg-[#242C37] border-[#2A3749] text-white placeholder-gray-400"
                    />
                  </div>
                </div>

                {/* Category Filter */}
                <div className="w-full lg:w-64">
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger className="bg-[#242C37] border-[#2A3749] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#242C37] border-[#2A3749] text-white">
                      {gameCategories.map(category => (
                        <SelectItem key={category.value} value={category.value}>
                          {category.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Sort */}
                <div className="w-full lg:w-48">
                  <Select value={sortOption} onValueChange={(value: SortOption) => setSortOption(value)}>
                    <SelectTrigger className="bg-[#242C37] border-[#2A3749] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#242C37] border-[#2A3749] text-white">
                      <SelectItem value="mentors-desc">Mais Mentores</SelectItem>
                      <SelectItem value="name-asc">A → Z</SelectItem>
                      <SelectItem value="name-desc">Z → A</SelectItem>
                      <SelectItem value="newest">Mais Recentes</SelectItem>
                      <SelectItem value="oldest">Mais Antigos</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* View Mode Toggle */}
                <div className="flex gap-2">
                  <Button
                    variant={viewMode === 'grid' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setViewMode('grid')}
                    className={viewMode === 'grid' ? 'bg-[#00B6F0] text-black' : 'bg-[#242C37] border-[#2A3749] text-white hover:bg-[#2A3749]'}
                  >
                    <Grid3X3 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={viewMode === 'list' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setViewMode('list')}
                    className={viewMode === 'list' ? 'bg-[#00B6F0] text-black' : 'bg-[#242C37] border-[#2A3749] text-white hover:bg-[#2A3749]'}
                  >
                    <List className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Active Filters & Clear */}
              {activeFiltersCount > 0 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#2A3749]">
                  <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-[#00B6F0]" />
                    <span className="text-sm text-gray-400">Filtros ativos:</span>
                    <Badge variant="secondary" className="bg-[#00B6F0]/20 text-[#00B6F0]">
                      {activeFiltersCount}
                    </Badge>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearAllFilters}
                    className="bg-transparent border-[#2A3749] text-gray-400 hover:bg-[#2A3749] hover:text-white"
                  >
                    Limpar Filtros
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Results Summary */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-gray-400">
              {filteredAndSortedGames.length > 0 ? (
                <>Mostrando <span className="text-[#00B6F0] font-semibold">{filteredAndSortedGames.length}</span> jogo{filteredAndSortedGames.length !== 1 ? 's' : ''}</>
              ) : (
                'Nenhum jogo encontrado'
              )}
            </p>
            {(sortOption.includes('name') || sortOption === 'mentors-desc') && (
              <div className="flex items-center gap-1 text-gray-400 text-sm">
                {sortOption === 'mentors-desc' ? (
                  <>
                    <Users className="h-4 w-4" />
                    Ordenado por mentores
                  </>
                ) : sortOption === 'name-asc' ? (
                  <>
                    <SortAsc className="h-4 w-4" />
                    Ordenado por nome
                  </>
                ) : (
                  <>
                    <SortDesc className="h-4 w-4" />
                    Ordenado por nome
                  </>
                )}
              </div>
            )}
          </div>

          {/* Games Grid/List */}
          {isLoading && (
            <div className="flex items-center justify-center w-full py-24">
              <div className="text-center">
                <Loader2 className="h-12 w-12 text-[#00B6F0] animate-spin mx-auto mb-4" />
                <p className="text-gray-400">Carregando jogos...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="text-red-500 p-6 bg-[#181D24] rounded-lg border border-red-800 text-center">
              <p className="font-semibold mb-2">Erro ao carregar jogos</p>
              <p className="text-sm">{String(error)}</p>
            </div>
          )}

          {data && filteredAndSortedGames.length === 0 && !isLoading && (
            <div className="text-center py-24">
              <div className="bg-[#181D24] rounded-lg border border-[#222A35] p-12 max-w-md mx-auto">
                <div className="w-16 h-16 bg-[#00B6F0]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="h-8 w-8 text-[#00B6F0]" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Nenhum jogo encontrado</h3>
                <p className="text-gray-400 mb-4">
                  Tente ajustar seus filtros ou buscar por outro termo
                </p>
                <Button
                  onClick={clearAllFilters}
                  variant="outline"
                  className="border-[#00B6F0] text-[#00B6F0] hover:bg-[#00B6F0]/10"
                >
                  Limpar Filtros
                </Button>
              </div>
            </div>
          )}

          {/* Games Display */}
          {filteredAndSortedGames.length > 0 && (
            <div className={viewMode === 'grid' 
              ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-8 lg:gap-10 xl:gap-12 justify-items-center" 
              : "space-y-4"
            }>
              {filteredAndSortedGames.map((game) => (
                <div key={game.id} className={viewMode === 'list' ? "w-full" : ""}>
                  {viewMode === 'grid' ? (
                    <GameCard
                      name={game.name}
                      coverUrl={game.cover_url}
                      gameId={game.id}
                      onClick={() => navigate(`/mentorias?game=${game.id}`)}
                    />
                  ) : (
                    <ListGameCard
                      game={game}
                      onClick={() => navigate(`/mentorias?game=${game.id}`)}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JogosPage;
